import apiClient from './client';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  username?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  roles: Role[];
}

export interface Role {
  id: number;
  name: string;
  description: string;
}

export interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  username?: string;
  roles?: string[];
  isActive?: boolean;
}

export interface UpdateUserData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  username?: string;
  roles?: string[];
  isActive?: boolean;
  profileData?: any;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
  sortBy?: string;
  order?: 'ASC' | 'DESC';
}

export interface UserStats {
  total: number;
  active: number;
  verified: number;
  locked: number;
  roleBreakdown: Record<string, number>;
}

class UserService {
  async getAllUsers(filters: UserFilters = {}) {
    const response = await apiClient.get('/users', { params: filters });
    return response.data;
  }

  async getUser(id: number) {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserData) {
    const response = await apiClient.post('/users', data);
    return response.data;
  }

  async updateUser(id: number, data: UpdateUserData) {
    const response = await apiClient.put(`/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number) {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  }

  async resetPassword(id: number, newPassword: string, sendEmail: boolean = true) {
    const response = await apiClient.post(`/users/${id}/reset-password`, {
      newPassword,
      sendEmail,
    });
    return response.data;
  }

  async sendPasswordResetLink(id: number) {
    const response = await apiClient.post(`/users/${id}/send-reset-link`);
    return response.data;
  }

  async unlockAccount(id: number) {
    const response = await apiClient.post(`/users/${id}/unlock`);
    return response.data;
  }

  async getUserStats(): Promise<{ success: boolean; data: UserStats }> {
    const response = await apiClient.get('/users/stats');
    return response.data;
  }

  async getAllRoles(): Promise<{ success: boolean; data: Role[] }> {
    const response = await apiClient.get('/users/roles');
    return response.data;
  }

  async bulkAssignRoles(userIds: number[], roles: string[], action: 'add' | 'replace' | 'remove' = 'add') {
    const response = await apiClient.post('/users/bulk/assign-roles', {
      userIds,
      roles,
      action,
    });
    return response.data;
  }

  async getUsersByRole(roleName: string): Promise<{ success: boolean; data: User[] }> {
    const response = await apiClient.get(`/users/by-role/${roleName}`);
    return response.data;
  }
}

export const userService = new UserService();
